import { getAnalyzerLines, pickAnalyzerLines } from '../lib/content.js';
import { playScanning, stopScanning, loadAudio } from '../lib/audio.js';
import { isVictorCorthals } from '../lib/specialCases.js';

const STATUS_COLORS = {
  OK: '#5aff8e', CONFIRMED: '#5aff8e', ALIGNED: '#5aff8e', RESONANT: '#5aff8e',
  FLAGGED: '#ffd75a', DETECTED: '#ffd75a', INCONCLUSIVE: '#ffd75a',
  EXCESSIVE: '#ff5a5a',
  FAILED: '#ff5a5a', NEGATIVE: '#ff5a5a', 'NOT FOUND': '#ff5a5a', ERROR: '#ff5a5a',
};

const VICTOR_LINES = [
  { text: "Cross-referencing Spotify history… only 'Wagon Wheel' on repeat", status: 'NEGATIVE' },
  { text: "Scanning for brunch proximity… 47 km to nearest", status: 'FAILED' },
  { text: "Inspecting Letterboxd activity… 0 A24 reviews", status: 'NEGATIVE' },
  { text: "Measuring finger-gun frequency… 0.0 per hour", status: 'NEGATIVE' },
  { text: "Loading homosexuality.dll… file not found", status: 'NOT FOUND' },
  { text: "Calibrating gaydar… signal lost", status: 'ERROR' },
  { text: "Scanning wardrobe… cargo shorts detected", status: 'FAILED' },
  { text: "Cross-referencing celestial frequency… conclusively dissonant", status: 'FAILED' },
  { text: "Checking iced coffee consumption… only black, piping hot", status: 'NEGATIVE' },
  { text: "Detecting Chappell Roan rotation… absent", status: 'NEGATIVE' },
  { text: "Verifying gym selfie ratio… well within normal range", status: 'NEGATIVE' },
  { text: "Triangulating Eurovision awareness… subject unaware", status: 'FAILED' },
];

function escapeHtml(s) {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export async function runAnalyzer(root, { name, durationMs = 10000, onComplete }) {
  const victor = isVictorCorthals(name);
  const liveColor = victor ? '#ff5a5a' : '#5aff8e';
  const initialStatus = victor
    ? 'ANOMALY DETECTED — 0 of 8 CRITERIA FAILED'
    : 'EVIDENCE ACCUMULATING — 0 of 8 CRITERIA';
  // Render DOM synchronously so callers can query elements immediately
  root.innerHTML = `
    <div class="forensic-page" data-test="analyzer">
      <header class="forensic-header">
        <span>● ANALYZING SUBJECT: ${escapeHtml(name).toUpperCase()}</span>
        <span class="forensic-spacer"></span>
        <span style="color:${liveColor}">[${victor ? 'ALERT' : 'LIVE'}]</span>
      </header>
      <div class="progress-bar" data-test="progress">
        <div class="progress-fill" data-test="progress-fill" style="width:0%"></div>
      </div>
      <div class="analyzer-lines" data-test="lines"></div>
      <div class="evidence-status" data-test="evidence">${initialStatus}</div>
    </div>
  `;

  await loadAudio();
  let pool;
  if (victor) {
    pool = VICTOR_LINES;
  } else {
    pool = await getAnalyzerLines();
  }
  const lines = pickAnalyzerLines(pool, 8);

  const progressFill = root.querySelector('[data-test=progress-fill]');
  const linesEl = root.querySelector('[data-test=lines]');
  const evidence = root.querySelector('[data-test=evidence]');

  if (reducedMotion()) {
    for (const l of lines) {
      const div = document.createElement('div');
      div.dataset.test = 'line';
      div.innerHTML = `> ${escapeHtml(l.text)}&nbsp;<span style="color:${STATUS_COLORS[l.status] || '#5a8eff'}">[${l.status}]</span>`;
      linesEl.appendChild(div);
    }
    progressFill.style.width = '100%';
    evidence.textContent = victor
      ? 'ANOMALY CONFIRMED — 8 of 8 CRITERIA FAILED'
      : 'EVIDENCE COMPILED — 8 of 8 CRITERIA';
    onComplete?.();
    return;
  }

  playScanning();
  const perLine = durationMs / lines.length;
  for (let i = 0; i < lines.length; i++) {
    await new Promise((r) => setTimeout(r, perLine * 0.85));
    const l = lines[i];
    const div = document.createElement('div');
    div.dataset.test = 'line';
    div.style.opacity = '0';
    div.innerHTML = `> ${escapeHtml(l.text)}&nbsp;<span style="color:${STATUS_COLORS[l.status] || '#5a8eff'}">[${l.status}]</span>`;
    linesEl.appendChild(div);
    requestAnimationFrame(() => { div.style.opacity = '1'; });
    progressFill.style.width = `${Math.round(((i + 1) / lines.length) * 100)}%`;
    evidence.textContent = victor
      ? `ANOMALY DETECTED — ${i + 1} of 8 CRITERIA FAILED`
      : `EVIDENCE ACCUMULATING — ${i + 1} of 8 CRITERIA`;
  }
  await new Promise((r) => setTimeout(r, 300));
  stopScanning();
  onComplete?.();
}
